import type { ClienteApi } from '../cliente';
import { ErrorApi } from '../problema';
import type { components } from '../tipos/plataforma';

type Esquema<N extends keyof components['schemas']> = components['schemas'][N];

export type CredencialTablet = Esquema<'DeviceCredential'>;
export type PersonaDeLaTablet = Esquema<'DeviceMember'>;
export type SesionEnLaTablet = Esquema<'TokenPair'>;

/**
 * Operaciones de la tablet del negocio (1.13), con su `operationId` y los tipos generados del contrato. Van con el cliente
 * de la tablet (`crearOps(() => credencial)`): sin ella, `pairDevice`; con ella, las demás.
 */
export function operacionesTablet(api: ClienteApi) {
    return {
        /** pairDevice (anónima): el código de 6 números de Equipo › Tablets; devuelve la credencial (solo esta vez). */
        pairDevice: (code: string) => api.pedir<CredencialTablet>('POST', '/v1/devices/pair', { cuerpo: { code } }),
        /**
         * listDeviceMembers (credencial de la tablet): «¿Quién eres?», sin emails ni roles (G178, G212), con la zona horaria
         * del negocio para «Bloqueado hasta las…» (`timezone`, G375).
         */
        listDeviceMembers: () => api.pedir<{ timezone: string; data: PersonaDeLaTablet[] }>('GET', '/v1/devices/current/members'),
        /** startMemberSessionOnDevice (credencial de la tablet): el PIN abre la sesión corta del miembro (10 min sin uso). */
        startMemberSessionOnDevice: (memberId: string, pin: string) =>
            api.pedir<SesionEnLaTablet>('POST', '/v1/devices/current/member-sessions', { cuerpo: { member_id: memberId, pin } }),
    };
}

/**
 * Un PIN incorrecto en `startMemberSessionOnDevice` (G873): `401 unauthenticated` con los intentos que quedan antes del
 * bloqueo (`attempts_remaining`, `PinIncorrectProblem`). Devuelve esos intentos (null si la API no los cuenta, p. ej. sin
 * PIN) o `undefined` si el error no es de PIN: entonces un `401` es la credencial de la tablet (revocada). El panel no
 * cuenta los intentos.
 */
export function intentosDelPin(e: unknown): number | null | undefined {
    if (!(e instanceof ErrorApi) || e.estado !== 401) {
        return undefined;
    }
    const problema = e.problema as Record<string, unknown>;
    const delPin = 'attempts_remaining' in problema || e.errores.some((x) => x.field === 'pin');
    if (!delPin) {
        return undefined;
    }
    const quedan = problema.attempts_remaining;
    return typeof quedan === 'number' ? quedan : null;
}
